/**
 * src/components/spatial/mapAggregateLayers.ts
 *
 * Single source of truth for GeoTrust's Phase 2 "aggregate" map layers —
 * the normalized `/api/map/*` layers that render through the deck.gl
 * overlay independently of the spatial-objects render pipeline.
 *
 * Both the data controller (`MapAggregateLayersController`) and the
 * inspector UI (`MapLayersPanel`) consume this list so the layer id, data
 * slice, WS channel base, fetcher, and legend never drift apart.
 */

import { mapApiClient } from '../../services/spatial/map/mapApiClient';
import { DECK_LAYER_IDS } from '../../services/spatial/deck/layerRegistry';
import { MAP_CHANNEL_BASE, type MapChannelBase } from '../../services/spatial/map/mapChannels';
import type { MapSliceKey } from './MapDataContext';
import type { MapReadParams, MapFeatureCollection } from '../../services/spatial/map/mapTypes';

export interface AggregateLayerDescriptor {
  /** deck layer id (also the `visibleLayerIds` entry gating the layer). */
  layerId: string;
  /** Which normalized state slice this layer's features live in. */
  slice: MapSliceKey;
  /** Singular camelCase WS channel base (see mapChannels). */
  channelBase: MapChannelBase;
  /** Human label for the Layers panel. */
  label: string;
  /** One-line description shown under the label. */
  description: string;
  /** Whether the layer is toggled on by default. */
  defaultVisible: boolean;
  /** Layer requires an authenticated key (hidden gracefully on 401/403). */
  requiresAuth?: boolean;
  /** Representative swatch colour (CSS) for the Layers-panel row. */
  swatch: string;
  /**
   * Fetch scope:
   *  - 'bbox'   → viewport-scoped; GeoLink rejects bbox area > ~100 deg²
   *               (`bbox_too_large`), so the controller skips these when the
   *               map is zoomed too far out, and refetches them on camera idle.
   *  - 'global' → NOT bbox-scoped (e.g. country-policies keys by country, not
   *               viewport); fetched once and refreshed only on the slow timer.
   */
  scope: 'bbox' | 'global';
  /** Extra query params merged into every fetch (e.g. `includeGeometry`). */
  params?: MapReadParams;
  /** REST fetcher. */
  fetch: (params: MapReadParams) => Promise<MapFeatureCollection>;
}

/**
 * Paint order matches the deck registry (choropleth/density underneath,
 * point layers on top). `defaultVisible` keeps the initial cost bounded:
 * only the cheap public layers fetch until the user opts into the rest.
 */
export const AGGREGATE_LAYERS: AggregateLayerDescriptor[] = [
  {
    layerId: DECK_LAYER_IDS.countryPolicy,
    slice: 'countries',
    channelBase: MAP_CHANNEL_BASE.policy,
    label: 'Country policy',
    description: 'Per-country policy-state choropleth',
    // Off by default: the main map ALREADY colours countries by policy state
    // via the native `countries-fill`/`countries-outline` overlay. Drawing this
    // translucent full-country deck choropleth on top traced a SECOND border
    // edge (GeoLink's country geometry diverges from the app's countries.geojson
    // when zoomed in), which read as a doubled/dotted border. Leave it as an
    // opt-in layer from the Map Layers panel instead of doubling the boundary.
    defaultVisible: false,
    // country-policies keys by country, not viewport — fetch the whole world
    // once (with geometry so we can draw the choropleth) rather than by bbox.
    scope: 'global',
    params: { includeGeometry: true },
    swatch: '#22c55e',
    fetch: (p) => mapApiClient.getCountryPolicies(p),
  },
  {
    layerId: DECK_LAYER_IDS.executionRules,
    slice: 'executionRules',
    channelBase: MAP_CHANNEL_BASE.executionRule,
    label: 'Execution rules',
    description: 'Rule boundary geometry',
    // Off by default: the main map ALREADY draws geofence/rule boundaries
    // natively (see App.tsx renderContractMarkers). Drawing this deck layer on
    // top traced a SECOND green boundary right next to the native one — the
    // "two parallel green lines per geofence". Leave it as an opt-in layer from
    // the Map Layers panel instead of doubling every geofence outline.
    defaultVisible: false,
    scope: 'bbox',
    swatch: '#f59e0b',
    fetch: (p) => mapApiClient.getExecutionRules(p),
  },
  {
    layerId: DECK_LAYER_IDS.agentPositions,
    slice: 'agents',
    channelBase: MAP_CHANNEL_BASE.agent,
    label: 'Agents',
    description: 'Live agent positions',
    defaultVisible: true,
    scope: 'bbox',
    swatch: '#38bdf8',
    fetch: (p) => mapApiClient.getAgents(p),
  },
  {
    layerId: DECK_LAYER_IDS.presenceCells,
    slice: 'presenceCells',
    channelBase: MAP_CHANNEL_BASE.presenceCell,
    label: 'Presence density',
    description: 'Aggregate presence grid (privacy-safe)',
    defaultVisible: false,
    scope: 'bbox',
    swatch: '#a855f7',
    fetch: (p) => mapApiClient.getPresenceCells(p),
  },
  {
    layerId: DECK_LAYER_IDS.dronePositions,
    slice: 'drones',
    channelBase: MAP_CHANNEL_BASE.drone,
    label: 'Drones',
    description: 'Live drone telemetry',
    defaultVisible: false,
    scope: 'bbox',
    swatch: '#fb923c',
    fetch: (p) => mapApiClient.getDrones(p),
  },
];

/** Look up a descriptor by its WS channel base (for delta routing). */
export function aggregateLayerByChannelBase(base: string): AggregateLayerDescriptor | undefined {
  return AGGREGATE_LAYERS.find((d) => d.channelBase === base);
}
